import { useBundle } from '@amzn/react-arb-tools';
import { useCallback } from 'react';
import { t } from 'src/utils/helpers';

/**
 * Hook that wraps useBundle and returns a stable `translate` function.
 *
 * Components call this hook internally with their own bundle name instead of
 * receiving a `bundle` prop from their parent. This keeps i18n concerns local
 * to each component and avoids prop-drilling the bundle down the tree.
 *
 * Pattern adopted from PromotionCentralExperienceUIWebsite.
 *
 * @param bundleName - ARB bundle name (e.g. 'molecules.campaignActionBar')
 * @returns `{ translate, isBundleLoading }`
 *
 * @example
 * const { translate } = useTranslate('molecules.campaignActionBar');
 * return <Button>{translate('create_button')}</Button>;
 */
export const useTranslate = (bundleName: string) => {
  const [bundle, isBundleLoading] = useBundle(bundleName);

  const translate = useCallback(
    (stringId: string, args?: Record<string, unknown>): string =>
      isBundleLoading || !bundle ? '' : t(bundle, stringId, args),
    [bundle, isBundleLoading]
  );

  return { isBundleLoading, translate };
};
